import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Progress } from './ui/progress';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { api } from '../utils/api';

const LeadFormDrawer = ({ open, onOpenChange, agentSlug = null }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_type: '',
    surface_m2: '',
    num_rooms: '',
    address: '',
    postal_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    has_sale_project: true,
    agent_slug: agentSlug
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.property_type && formData.postal_code && formData.postal_code.length === 4;
      case 2:
        return formData.surface_m2 && formData.surface_m2 > 0;
      case 3:
        return true; // All optional
      case 4:
        return formData.first_name && formData.last_name && formData.email && formData.phone;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    try {
      // Capture UTM parameters
      const urlParams = new URLSearchParams(window.location.search);
      const submitData = {
        ...formData,
        surface_m2: parseInt(formData.surface_m2),
        num_rooms: formData.num_rooms ? parseInt(formData.num_rooms) : null,
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        referrer: document.referrer || null
      };

      await api.createLead(submitData);
      
      toast.success('Demande envoyée !', {
        description: 'Un expert vous contactera sous 24h.'
      });

      // Reset form
      setFormData({
        property_type: '',
        surface_m2: '',
        num_rooms: '',
        address: '',
        postal_code: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        has_sale_project: true,
        agent_slug: agentSlug
      });
      setStep(1);
      onOpenChange(false);
    } catch (error) {
      toast.error('Erreur', {
        description: 'Impossible d\'envoyer la demande. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  const FormContent = () => (
    <div className="p-6">
      {/* Progress */}
      <div className="sticky top-0 bg-white/95 backdrop-blur border-b px-4 py-3 -mx-6 -mt-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Étape {step}/{totalSteps}</span>
          <button
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground"
            data-testid="close-form-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <Progress value={progress} className="h-2" data-testid="form-progress" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Property Type & Location */}
        {step === 1 && (
          <div className="space-y-4" data-testid="form-step-1">
            <div>
              <Label htmlFor="property_type">Type de bien *</Label>
              <Select
                value={formData.property_type}
                onValueChange={(value) => updateField('property_type', value)}
              >
                <SelectTrigger id="property_type" data-testid="property-type-select">
                  <SelectValue placeholder="Sélectionnez le type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Appartement">Appartement</SelectItem>
                  <SelectItem value="Maison">Maison</SelectItem>
                  <SelectItem value="Immeuble">Immeuble</SelectItem>
                  <SelectItem value="Terrain">Terrain</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="postal_code">Code postal *</Label>
              <Input
                id="postal_code"
                type="text"
                placeholder="1003"
                maxLength={4}
                value={formData.postal_code}
                onChange={(e) => updateField('postal_code', e.target.value)}
                data-testid="postal-code-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Code postal suisse (4 chiffres)
              </p>
            </div>

            <div>
              <Label htmlFor="address">Adresse du bien (optionnel)</Label>
              <Input
                id="address"
                type="text"
                placeholder="Rue du Lac 45"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                data-testid="address-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Property Details */}
        {step === 2 && (
          <div className="space-y-4" data-testid="form-step-2">
            <div>
              <Label htmlFor="surface_m2">Surface en m² *</Label>
              <Input
                id="surface_m2"
                type="number"
                placeholder="120"
                min="1"
                value={formData.surface_m2}
                onChange={(e) => updateField('surface_m2', e.target.value)}
                data-testid="surface-input"
              />
            </div>

            <div>
              <Label htmlFor="num_rooms">Nombre de pièces (optionnel)</Label>
              <Input
                id="num_rooms"
                type="number"
                placeholder="4"
                min="0"
                value={formData.num_rooms}
                onChange={(e) => updateField('num_rooms', e.target.value)}
                data-testid="num-rooms-input"
              />
            </div>
          </div>
        )}

        {/* Step 3: Project */}
        {step === 3 && (
          <div className="space-y-4" data-testid="form-step-3">
            <div>
              <Label>Avez-vous un projet de vente ?</Label>
              <div className="flex gap-4 mt-2">
                <Button
                  type="button"
                  variant={formData.has_sale_project ? 'default' : 'outline'}
                  onClick={() => updateField('has_sale_project', true)}
                  className="flex-1"
                  data-testid="has-project-yes"
                >
                  Oui
                </Button>
                <Button
                  type="button"
                  variant={!formData.has_sale_project ? 'default' : 'outline'}
                  onClick={() => updateField('has_sale_project', false)}
                  className="flex-1"
                  data-testid="has-project-no"
                >
                  Non
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Même sans projet immédiat, nous pouvons vous donner une estimation.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Contact */}
        {step === 4 && (
          <div className="space-y-4" data-testid="form-step-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+41 79 123 45 67"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                data-testid="phone-input"
              />
            </div>

            <div className="bg-muted/50 p-3 rounded-md">
              <p className="text-xs text-muted-foreground">
                🔒 Vos données sont protégées et ne seront jamais partagées.
                En soumettant ce formulaire, vous acceptez d'être contacté par un expert immobilier.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 pt-4">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              className="flex-1"
              data-testid="form-prev-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
          )}

          {step < totalSteps ? (
            <Button
              type="button"
              onClick={nextStep}
              disabled={!validateStep()}
              className="flex-1"
              data-testid="form-next-button"
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={!validateStep() || loading}
              className="flex-1"
              data-testid="form-submit-button"
            >
              {loading ? (
                'Envoi en cours...'
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Envoyer ma demande
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );

  // Mobile: Drawer, Desktop: Dialog
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="lead-form-drawer">
          <DrawerHeader>
            <DrawerTitle>Demande d'estimation gratuite</DrawerTitle>
          </DrawerHeader>
          <FormContent />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle>Demande d'estimation gratuite</DialogTitle>
          <DialogDescription>
            Remplissez ce formulaire en quelques étapes pour recevoir votre estimation immobilière personnalisée.
          </DialogDescription>
        </DialogHeader>
        <FormContent />
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormDrawer;
